import { useEffect, useRef } from 'react';
import { Field } from './fields/Field';

interface Props {
  siteKey: string | null;
  errors: string[];
  /** Receives the v2 response, or null when it expires or errors. Both widgets have their own
   *  controller, so the callback is passed in rather than pulled from a form-specific context. */
  onToken: (token: string | null) => void;
}

interface Grecaptcha {
  render: (el: HTMLElement, params: Record<string, unknown>) => number;
  reset: (id?: number) => void;
}
declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
    __cbfRecaptchaReady?: Array<() => void>;
  }
}

const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=__cbfRecaptchaOnload';

function whenReady(cb: () => void): void {
  if (window.grecaptcha?.render) return cb();
  window.__cbfRecaptchaReady = window.__cbfRecaptchaReady ?? [];
  window.__cbfRecaptchaReady.push(cb);
  if (!document.querySelector(`script[src^="https://www.google.com/recaptcha/api.js"]`)) {
    (window as unknown as Record<string, unknown>)['__cbfRecaptchaOnload'] = () => {
      const list = window.__cbfRecaptchaReady ?? [];
      window.__cbfRecaptchaReady = [];
      list.forEach((fn) => fn());
    };
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
}

/** reCAPTCHA v2 checkbox, rendered only when the tenant requires it. */
export function Recaptcha({ siteKey, errors, onToken }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const widget = useRef<number | null>(null);

  useEffect(() => {
    if (!siteKey || !host.current) return;
    let cancelled = false;
    const el = host.current;
    whenReady(() => {
      if (cancelled || !window.grecaptcha || widget.current !== null) return;
      widget.current = window.grecaptcha.render(el, {
        sitekey: siteKey,
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => onToken(null),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [siteKey, onToken]);

  return (
    <Field fieldKey="recaptcha_token" label="Security check" required errors={siteKey ? errors : ['Online booking is temporarily unavailable. Please contact us to book.']} group>
      {() => <div className="cbf-recaptcha" ref={host} data-cbf-field="recaptcha_token" tabIndex={-1} />}
    </Field>
  );
}
